/**
 * WIREDESK Portal Kit: contrato tipado do PAINEL.
 *
 * Duas camadas, de donos diferentes:
 *
 * 1. As CONFIGURAÇÕES por coluna e por bloco (`ConfigTempo`, `Marca`, `Rodape`...)
 *    espelham `docs/portal-kit/manifesto.schema.json` (ADR 0024, congelado). O
 *    painel edita exatamente o que o plugin lê; campo que não está no schema
 *    não existe aqui.
 *
 * 2. A API ADMINISTRATIVA (`/api/v1/config/portal`, JWT de admin do tenant) NÃO
 *    é coberta pelo ADR 0024 ("cada um tem o seu próprio PR"). O formato abaixo
 *    (`PortalSiteResumo`, `PortalSiteDetalhe`, `SecaoPortal`, `Conferencia`...) é
 *    o que a TELA espera, escrito antes da API existir. A frente 1A (backend) o
 *    implementa; qualquer divergência se resolve neste arquivo e em
 *    `services/portal.ts`, que são o único ponto de contato.
 *
 *    Resumo das rotas (todas sob `/config/portal`):
 *      GET    /sites                              -> PortalSiteResumo[]
 *      GET    /sites/{site_id}                    -> PortalSiteDetalhe
 *      PUT    /sites/{site_id}/colunas/{chave}    -> SecaoPortal   (If-Match: "<versao>"; 412 se divergir)
 *      DELETE /sites/{site_id}/colunas/{chave}    -> SecaoPortal   (volta ao padrão herdado; If-Match)
 *      GET    /sites/{site_id}/manifesto          -> ManifestoResolvido
 *      GET    /sites/{site_id}/conferencia        -> Conferencia
 *      POST   /sites/{site_id}/token              -> TokenGerado   (revoga o anterior; o segredo vem uma vez só)
 *      DELETE /sites/{site_id}/token              -> 204
 *      PATCH  /sites/{site_id}/plugin             -> PortalSiteDetalhe  ({ atualizacao_automatica })
 *      GET    /municipios?q=&uf=&limite=          -> Municipio[]
 *      GET    /plugin/pacote                      -> application/zip
 *
 *    `chave` é uma das 13 do vocabulário fechado: as nove colunas e os quatro
 *    blocos (`marca`, `rodape`, `seo`, `conteudo`). 422 devolve a lista de campos
 *    com problema; 403 é feature `portal_kit` fora do plano; 404 na listagem
 *    significa que a API não está instalada nesta instância.
 */

// ─── vocabulário fechado ─────────────────────────────────────────────────────

export const COLUNAS = [
  'tempo',
  'mapa',
  'agro',
  'mercado',
  'horoscopo',
  'loterias',
  'futebol',
  'servicos',
  'fonte',
] as const;
export type ColunaId = (typeof COLUNAS)[number];

export const BLOCOS = ['marca', 'rodape', 'seo', 'conteudo'] as const;
export type BlocoId = (typeof BLOCOS)[number];

export type SecaoId = ColunaId | BlocoId;

/** As cinco colunas que têm endereço próprio (`config.base`) e, portanto, rotas. */
export const COLUNAS_COM_BASE = ['tempo', 'agro', 'mercado', 'horoscopo', 'futebol'] as const;
export type ColunaComBase = (typeof COLUNAS_COM_BASE)[number];

export type ServidaPor = 'tema' | 'plugin';

/** De onde vem a configuração efetiva: padrão do código ⊕ tenant ⊕ site. */
export type OrigemConfig = 'site' | 'tenant' | 'padrao';

// ─── configuração por coluna (espelha o schema) ──────────────────────────────

export type Uf =
  | 'AC'
  | 'AL'
  | 'AP'
  | 'AM'
  | 'BA'
  | 'CE'
  | 'DF'
  | 'ES'
  | 'GO'
  | 'MA'
  | 'MT'
  | 'MS'
  | 'MG'
  | 'PA'
  | 'PB'
  | 'PR'
  | 'PE'
  | 'PI'
  | 'RJ'
  | 'RN'
  | 'RS'
  | 'RO'
  | 'RR'
  | 'SC'
  | 'SP'
  | 'SE'
  | 'TO';

/** Município escolhido pelo site (cidade do tempo, praça do agro). */
export interface Local {
  slug: string;
  nome: string;
  uf: Uf;
  ibge: string;
  lat: number;
  lon: number;
}

/** Linha do catálogo de 5.570 municípios (a busca da API devolve isto). */
export type Municipio = Local;

export interface TextosPagina {
  indice_titulo: string;
  indice_descricao: string;
  item_titulo: string;
  item_descricao: string;
}

export interface ConfigTempo {
  base: string;
  categoria: string;
  cidades: Local[];
  dias_previsao: number;
  cache_s: number;
  textos: TextosPagina;
}

export interface ConfigMapa {
  avisos_inmet: boolean;
  grau_minimo: number;
  cache_avisos_s: number;
  condicao_severa: boolean;
}

export interface LimiaresAgro {
  geada: { risco_ate_c: number; alto_ate_c: number };
  pulverizacao: {
    vento_max_kmh: number;
    umidade_min_pct: number;
    temp_max_c: number;
    chuva_max_mm: number;
  };
}

export interface ConfigAgro {
  base: string;
  pracas: Local[];
  dias_previsao: number;
  dias_passados: number;
  cache_s: number;
  limiares: LimiaresAgro;
  textos: TextosPagina;
}

export type TipoAtivo = 'indice' | 'cambio';

export interface AtivoTicker {
  tipo: TipoAtivo;
  ativo: string;
  rotulo: string;
}

export interface ConfigMercado {
  base: string;
  titulo: string;
  descricao: string;
  ticker: AtivoTicker[];
  painel_materia: string[];
  cache: { painel_s: number; ativo_s: number; indicadores_s: number };
  atraso_declarado_min: number;
  idade_maxima_s: number;
  manchetes_limite: number;
  indicadores_bcb: boolean;
  prefixo_slug_diaria: string;
}

export type SlugSigno =
  | 'aries'
  | 'touro'
  | 'gemeos'
  | 'cancer'
  | 'leao'
  | 'virgem'
  | 'libra'
  | 'escorpiao'
  | 'sagitario'
  | 'capricornio'
  | 'aquario'
  | 'peixes';

export interface Signo {
  slug: SlugSigno;
  nome: string;
  simbolo: string;
  periodo: string;
}

export interface ConfigHoroscopo {
  base: string;
  categoria: string;
  signos: Signo[];
  texto: { pagina_pai_slug: string | null; prefixo_slug_post: string };
  previa_max_caracteres: number;
  textos: TextosPagina;
}

export interface Modalidade {
  slug: string;
  nome: string;
}

export interface ConfigLoterias {
  categoria: string;
  modalidades: Modalidade[];
  outros_resultados_max: number;
}

export type IdSerie = 'a' | 'b' | 'c';

export interface SerieFutebol {
  id: IdSerie;
  slug: string;
  nome: string;
}

export const PAGINAS_FUTEBOL = ['rodada', 'estatisticas', 'jogo', 'clube', 'atleta'] as const;
export type PaginaFutebol = (typeof PAGINAS_FUTEBOL)[number];

export interface ConfigFutebol {
  base: string;
  categoria: string;
  series: SerieFutebol[];
  paginas: PaginaFutebol[];
  atraso_alerta_min: number;
  cache: { tabela_s: number; detalhe_s: number };
  imagens: { espelho_local: boolean; tamanhos_px: number[] };
  home: { serie: IdSerie; linhas: number };
}

export type AlvoFaixa = { caminho: string } | { categoria: string } | { url: string };

export interface ItemFaixa {
  chave: string;
  rotulo: string;
  alvo: AlvoFaixa;
}

export const CARTOES_HOME = ['tempo', 'mercado', 'horoscopo', 'futebol'] as const;
export type CartaoHome = (typeof CARTOES_HOME)[number];

export interface ConfigServicos {
  faixa: ItemFaixa[];
  cartoes_home: CartaoHome[];
  ticker: boolean;
  titulo_home: string;
}

export type AcaoSemFonte = 'avisar' | 'rascunho';

export interface ConfigFonte {
  exigir_fonte: boolean;
  acao_sem_fonte: AcaoSemFonte;
  categorias_isentas: string[];
  rotulo: string;
  meta_chave: '_wd_fontes';
  meta_legadas: string[];
}

// ─── blocos ──────────────────────────────────────────────────────────────────

export interface CoresMarca {
  primaria: string;
  secundaria: string;
  destaque: string;
  texto: string;
  fundo: string;
  positivo: string;
  negativo: string;
}

export interface Marca {
  nome: string;
  sigla: string;
  lema: string | null;
  edicao: string | null;
  logo: string | null;
  cores: CoresMarca;
  dateline: { ativo: boolean; praca_padrao: string; modelo: string };
  autor_padrao: string;
}

export type ModoRodape = 'completo' | 'selo' | 'os_dois';
export type FormatoHora = 'dia_mes_hora' | 'hora';

export interface Rodape {
  modo: ModoRodape;
  hora: { exibir: boolean; rotulo: string; formato: FormatoHora };
  atraso: { exibir: boolean; rotulo: string; alerta_acima_min: number };
  fonte: { exibir: boolean; rotulo: string };
  selo: { texto: string; url: string | null; vezes_por_pagina: 1 };
}

export type ModoSeo = 'auto' | 'proprio' | 'desligado';

export const TIPOS_NOINDEX = [
  'busca',
  '404',
  'tag',
  'autor',
  'data',
  'anexo',
  'categoria_vazia',
] as const;
export type TipoNoindex = (typeof TIPOS_NOINDEX)[number];

export interface Seo {
  modo: ModoSeo;
  jsonld_unico: boolean;
  idioma_og: string;
  imagem_padrao: string | null;
  organizacao: {
    tipo: 'NewsMediaOrganization' | 'Organization';
    politica_editorial_slug: string | null;
    expediente_slug: string | null;
    same_as: string[];
  };
  perenes_categorias: string[];
  pereciveis: {
    categorias: { categoria: string; dias: number }[];
    prefixos_slug: { prefixo: string; dias: number }[];
  };
  noindex: TipoNoindex[];
  llms_txt: boolean;
}

export interface Conteudo {
  categorias_noticia: string[];
  sem_repeticao: {
    ativo: boolean;
    janela_arquivo_h: number;
    teto_arquivo: number;
    termos_mesma_noticia: number;
    distancia_foto_max: number;
    fator_busca: number;
  };
}

// ─── seção = coluna ou bloco, com versão ─────────────────────────────────────

export interface ConfigPorSecao {
  tempo: ConfigTempo;
  mapa: ConfigMapa;
  agro: ConfigAgro;
  mercado: ConfigMercado;
  horoscopo: ConfigHoroscopo;
  loterias: ConfigLoterias;
  futebol: ConfigFutebol;
  servicos: ConfigServicos;
  fonte: ConfigFonte;
  marca: Marca;
  rodape: Rodape;
  seo: Seo;
  conteudo: Conteudo;
}

/** Uma das nove colunas: liga/desliga, quem serve e a config. */
export interface ColunaPortal<C extends ColunaId = ColunaId> {
  chave: C;
  /** Versão otimista. Vai em `If-Match` ao gravar; 412 quando outra pessoa gravou antes. */
  versao: number;
  origem: OrigemConfig;
  ativa: boolean;
  servida_por: ServidaPor;
  sitemap: boolean;
  config: ConfigPorSecao[C];
}

/** Um dos quatro blocos (marca, rodapé, SEO, conteúdo): só config. */
export interface BlocoPortal<B extends BlocoId = BlocoId> {
  chave: B;
  versao: number;
  origem: OrigemConfig;
  config: ConfigPorSecao[B];
}

export type SecaoPortal = ColunaPortal | BlocoPortal;

/** O que se envia no PUT de uma coluna. Os blocos mandam só `config`. */
export interface CorpoColuna<C extends ColunaId = ColunaId> {
  ativa: boolean;
  servida_por: ServidaPor;
  sitemap: boolean;
  config: ConfigPorSecao[C];
}
export interface CorpoBloco<B extends BlocoId = BlocoId> {
  config: ConfigPorSecao[B];
}
export type CorpoSecao = CorpoColuna | CorpoBloco;

export function ehColuna(chave: SecaoId): chave is ColunaId {
  return (COLUNAS as readonly string[]).includes(chave);
}

// ─── instalação, plugin e token ──────────────────────────────────────────────

export type PluginDeSeo = 'yoast' | 'rankmath' | 'aioseo' | 'seopress' | 'nenhum';

/** O que o plugin informa em cabeçalhos a cada leitura do manifesto (ADR 0024, D-e). */
export interface InstalacaoPlugin {
  versao: string;
  wp: string | null;
  php: string | null;
  tema: string | null;
  seo: PluginDeSeo | null;
  /** Colunas que o plugin está servindo agora. */
  servidas: ColunaId[];
  /** Ids de rota cujo endereço já era de outro conteúdo do WordPress. */
  conflitos: string[];
  /** Última leitura do manifesto, inclusive as que deram 304. */
  ultima_leitura_em: string;
}

export interface OfertaPlugin {
  versao_atual: string;
  versao_minima: string;
  atualizacao_automatica: boolean;
}

export interface TokenDoSite {
  /** Só o prefixo do token; o segredo nunca volta. */
  prefixo: string;
  criado_em: string;
  ultimo_uso_em: string | null;
}

export interface TokenGerado extends TokenDoSite {
  /** O segredo inteiro. Aparece uma vez só, na resposta do POST. */
  token: string;
}

// ─── sites ───────────────────────────────────────────────────────────────────

export interface PortalSiteResumo {
  site_id: string;
  slug: string;
  nome: string;
  url: string;
  colunas_ativas: ColunaId[];
  colunas_no_plugin: ColunaId[];
  instalacao: InstalacaoPlugin | null;
  plugin: OfertaPlugin;
  token: TokenDoSite | null;
}

export interface SignoDef {
  slug: SlugSigno;
  nome: string;
  simbolo: string;
  periodo: string;
}

/** Catálogos do produto que o painel precisa para re-adicionar o que foi removido. */
export interface CatalogosPortal {
  signos: SignoDef[];
  series: SerieFutebol[];
}

export type SecoesDoSite = { [C in ColunaId]: ColunaPortal<C> } & {
  [B in BlocoId]: BlocoPortal<B>;
};

export interface PortalSiteDetalhe extends PortalSiteResumo {
  secoes: SecoesDoSite;
  catalogos: CatalogosPortal;
}

// ─── manifesto e conferência ─────────────────────────────────────────────────

export interface ParamLista {
  tipo: 'lista';
  valores: string[];
}
export interface ParamPadrao {
  tipo: 'padrao';
  regex: string;
}

export interface RotaManifesto {
  id: string;
  modulo: ColunaComBase;
  padrao: string;
  itens: Record<string, ParamLista | ParamPadrao>;
  resposta: 'pagina' | 'redireciona';
  destino: string | null;
}

/** O manifesto resolvido: o painel só lê o que mostra; o formato inteiro é o do schema. */
export interface ManifestoResolvido {
  schema: string;
  etag: string;
  rotas: RotaManifesto[];
  [outro: string]: unknown;
}

export interface ItemConferencia {
  id: string;
  coluna: ColunaId;
  rotulo: string;
  /** Hora do dado (as_of), ISO 8601. Nulo = a fonte não entregou. */
  as_of: string | null;
  atraso_min: number | null;
  /** Acima disto o item é atenção. Vem do rodapé do site ou da coluna que declara o seu. */
  atraso_alerta_min: number;
  fonte: string | null;
  motivo: string | null;
}

export interface Conferencia {
  gerada_em: string;
  itens: ItemConferencia[];
}

// ─── erros de validação do servidor ──────────────────────────────────────────

export interface ErroDeCampo {
  /** Caminho no JSON enviado, por exemplo `config.cidades.2.ibge`. */
  campo: string;
  mensagem: string;
}
